import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format, parseISO } from 'date-fns';
import { FiDownload, FiSearch, FiUser, FiFileText, FiCalendar, FiHelpCircle } from 'react-icons/fi';

const COLORS = {
  primary: "#0760ad",
  secondary: "#118a94",
  primaryLight: "#098a48",
  white: "#FFFFFF",
  darkText: "#1a365d",
  lightText: "#4a5568",
  lightGray: "#f8fafc",
  borderGray: "#e2e8f0"
};

const MedicalReportViewer = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const apiUrl = import.meta.env.VITE_API_URL;

  const handleDownloadReport = (reportUrl) => {
    if (reportUrl) {
      window.open(reportUrl, "_blank");
    } else {
      alert("No report available to download.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setReports([]);
    setIsLoading(true);

    try {
      const response = await axios.get(
        `${apiUrl}/medical-reports/?phone_number=${phoneNumber}`
      );

      if (response.data && response.data.length > 0) {
        setReports(response.data);
        setFilteredReports(response.data);
      } else {
        throw new Error("No reports found");
      }
    } catch (error) {
      setErrorMessage("No medical reports found for this phone number.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = () => {
    if (!startDate && !endDate) {
      setFilteredReports(reports);
      return;
    }

    const filtered = reports.filter(report => {
      const reportDate = new Date(report.created_at);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && end) {
        return reportDate >= start && reportDate <= end;
      } else if (start) {
        return reportDate >= start;
      } else if (end) {
        return reportDate <= end;
      }
      return true;
    });

    setFilteredReports(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [startDate, endDate, reports]);

  const handleInputChange = (e) => {
    setPhoneNumber(e.target.value);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div 
        className="py-20 px-4 sm:px-6 lg:px-8 text-center"
        style={{ background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)` }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Medical Report Portal
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Access your medical examination results securely using your phone number
          </p>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Form Panel */}
          <div className="p-8">
            <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
              Retrieve Your Reports
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="form-control relative">
                  <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone-number"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      placeholder="Enter your registered phone number"
                      value={phoneNumber}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 px-6 rounded-md font-medium text-white shadow-sm hover:shadow-md transition-all flex items-center justify-center"
                  style={{ backgroundColor: COLORS.primary }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching...
                    </>
                  ) : (
                    <>
                      <FiSearch className="mr-2" />
                      View Medical Reports
                    </>
                  )}
                </motion.button>
              </div>
            </form>

            {/* Date Filter */}
            {reports.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.primary }}>
                  Filter Reports by Date
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">From Date</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiCalendar className="text-gray-400" />
                      </div>
                      <input
                        type="date"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">To Date</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiCalendar className="text-gray-400" />
                      </div>
                      <input
                        type="date"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Reports List */}
            {errorMessage && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-6 rounded-lg border-2 mt-8"
                style={{ 
                  borderColor: COLORS.primary,
                  backgroundColor: COLORS.lightGray
                }}
              >
                <div className="flex items-center mb-4">
                  <svg className="w-8 h-8 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <h3 className="text-xl font-bold" style={{ color: COLORS.darkText }}>
                    No Reports Found
                  </h3>
                </div>
                <p className="mb-6" style={{ color: COLORS.darkText }}>
                  {errorMessage}
                </p>
                <div className="text-sm" style={{ color: COLORS.darkText }}>
                  <p>Please check your phone number or contact support if you believe this is an error.</p>
                </div>
              </motion.div>
            )}

            {filteredReports.length > 0 && (
              <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: COLORS.primary }}>
                    Your Medical Reports
                  </h3>
                  <span className="text-sm text-gray-500">{filteredReports.length} reports found</span>
                </div>
                
                <div className="space-y-4">
                  {filteredReports.map((report, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-6 border rounded-lg hover:shadow-md transition-shadow"
                      style={{ borderColor: COLORS.borderGray }}
                    >
                      <div className="flex flex-col md:flex-row justify-between">
                        <div className="mb-4 md:mb-0">
                          <h4 className="font-medium" style={{ color: COLORS.darkText }}>
                            {report.title || "Medical Report"}
                          </h4>
                          <div className="flex items-center mt-2 text-sm text-gray-500">
                            <FiCalendar className="mr-2" />
                            <span>{format(parseISO(report.created_at), 'MMMM d, yyyy')}</span>
                          </div>
                          {report.description && (
                            <p className="text-sm mt-2" style={{ color: COLORS.lightText }}>
                              {report.description}
                            </p>
                          )}
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDownloadReport(report.file)}
                          className="px-4 py-2 rounded-md font-medium flex items-center self-start md:self-center"
                          style={{ 
                            backgroundColor: COLORS.lightGray,
                            color: COLORS.primary
                          }}
                        >
                          <FiDownload className="mr-2" />
                          Download
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>
            Need Help With Your Reports?
          </h3>
          <p className="text-gray-600 mb-6 max-w-3xl mx-auto">
            Our medical team is available to help you understand your test results and provide guidance.
          </p>
          <motion.a
            href="/contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 rounded-md font-medium text-white shadow-sm inline-flex items-center"
            style={{ backgroundColor: COLORS.primary }}
          >
            <FiHelpCircle className="mr-2" />
            Contact Our Support
          </motion.a>
        </div>
      </div>
    </div>
  );
};

export default MedicalReportViewer;